import { Badge, SideNavigation, SideNavigationProps } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'src/contexts/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  fetchReportsConfig,
  selectActiveReportsConfig,
  selectReportsConfigHasAttemptedFetch,
  selectReportsConfigIsLoading,
} from 'src/store/slices/reportsConfigSlice';
import { ReportsConfiguration } from 'src/types/admin/reportsConfig.types';
import { getUserAccessFlags } from 'src/utils/authUtils';
import { navigatePreservingParams } from 'src/utils/navigationUtils';
import { usePermissions } from 'src/hooks/usePermissions';
import { canUserAccessReport } from 'src/utils/reportVisibilityUtils';
import { buildReportsSectionGroup } from './utils/reportsNavigationUtils';
import { ADMIN_ROUTES, NAV_ITEMS } from '../../constants/routingConstants';
import { getFilteredAdminNavSections } from '../../constants/routingConstants';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Filters reports based on user's access permissions.
 *
 * @param reports - Array of all reports
 * @param userPermission - Current user's permission level
 * @returns Array of reports the user can access
 */
const filterReportsByAccess = (
  reports: ReportsConfiguration[],
  userPermission: ReturnType<typeof usePermissions>['userPermission']
): ReportsConfiguration[] => {
  return reports.filter((report) => canUserAccessReport(report.report_visibility_level, userPermission));
};

/**
 * Builds the Programs section with all program-related navigation items.
 *
 * @returns Section item for Programs
 */
const buildProgramsSection = (): SideNavigationProps.Section => ({
  type: 'section',
  text: 'Programs',
  items: [
    {
      type: 'link',
      text: NAV_ITEMS.PROGRAM_ROADMAP.text,
      href: NAV_ITEMS.PROGRAM_ROADMAP.href,
    },
    {
      type: 'link',
      text: NAV_ITEMS.VIEW_EDIT_PROGRAMS.text,
      href: NAV_ITEMS.VIEW_EDIT_PROGRAMS.href,
    },
    {
      type: 'link',
      text: NAV_ITEMS.BULK_EDIT.text,
      href: NAV_ITEMS.BULK_EDIT.href,
    },
    {
      type: 'link',
      text: NAV_ITEMS.MANAGE_ATTRIBUTES.text,
      href: NAV_ITEMS.MANAGE_ATTRIBUTES.href,
    },
    {
      type: 'link',
      text: NAV_ITEMS.CHANGE_HISTORY.text,
      href: NAV_ITEMS.CHANGE_HISTORY.href,
    },
  ],
});

/**
 * Builds the Admin Tools section based on user permissions.
 *
 * @param hasFullAdminAccess - Whether user has full admin access
 * @param hasOverrideEscapeHatch - Whether user has override escape hatch access
 * @returns Section item for Admin Tools
 */
const buildAdminToolsSection = (
  hasFullAdminAccess: boolean,
  hasOverrideEscapeHatch: boolean
): SideNavigationProps.Section => {
  const adminItems: SideNavigationProps.Link[] = [];

  // Scenario Snapshots - only for full admin access
  if (hasFullAdminAccess) {
    adminItems.push({
      type: 'link',
      text: NAV_ITEMS.SCENARIO_SNAPSHOTS.text,
      href: NAV_ITEMS.SCENARIO_SNAPSHOTS.href,
    });
  }

  // Access Management - visible to full admin AND override escape hatch
  adminItems.push({
    type: 'link',
    text: ADMIN_ROUTES.ACCESS_MANAGEMENT.text,
    href: ADMIN_ROUTES.ACCESS_MANAGEMENT.href,
    ...(hasOverrideEscapeHatch && {
      info: <Badge color="blue">Dev Override</Badge>,
    }),
  });

  return {
    type: 'section',
    text: 'Admin Tools',
    items: adminItems,
  };
};

/**
 * Builds the Support section with support-related navigation items.
 *
 * @returns Section item for Support
 */
const buildSupportSection = (): SideNavigationProps.Section => ({
  type: 'section',
  text: 'Support',
  items: [
    {
      type: 'link',
      text: NAV_ITEMS.FINANCE_CONTACTS.text,
      href: NAV_ITEMS.FINANCE_CONTACTS.href,
    },
    {
      type: 'link',
      text: 'Verity Support',
      href: '/verity-support',
    },
  ],
});

const HomePageSideNavigation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  // Get user access flags
  const { user } = useAuth();
  const { isInitiallyDeveloper } = getUserAccessFlags(user);
  const { isTechAdmin, isVerityAdmin, userPermission } = usePermissions();

  // Full admin access: can see all admin tools (Scenario Snapshots, Access Management, etc.)
  const hasFullAdminAccess = isVerityAdmin || isTechAdmin;
  // Override escape hatch: developer in DB-override mode can ONLY see Access Management
  const hasOverrideEscapeHatch = isInitiallyDeveloper && !hasFullAdminAccess;
  // Show admin section if user has full access OR escape hatch access
  const showAdminSection = hasFullAdminAccess || hasOverrideEscapeHatch;

  // Get active reports from Redux store using selectors
  const activeReports = useAppSelector(selectActiveReportsConfig);
  const hasAttemptedFetch = useAppSelector(selectReportsConfigHasAttemptedFetch);
  const isLoadingConfig = useAppSelector(selectReportsConfigIsLoading);

  // Fetch reports on mount if not already fetched or loading
  useEffect(() => {
    if (!hasAttemptedFetch && !isLoadingConfig) {
      dispatch(fetchReportsConfig({}));
    }
  }, [dispatch, hasAttemptedFetch, isLoadingConfig]);

  /**
   * Build navigation items using utility functions.
   * Follows Open/Closed Principle - easy to extend with new sections.
   */
  const navigationItems: SideNavigationProps.Item[] = useMemo(() => {
    const items: SideNavigationProps.Item[] = [];

    // Add Programs section
    items.push(buildProgramsSection());

    // Add Reports section-group with dynamic grouped items
    const accessibleReports = filterReportsByAccess(activeReports, userPermission);
    items.push(buildReportsSectionGroup(accessibleReports));

    // Add Admin Tools section if user has access
    if (showAdminSection) {
      items.push(buildAdminToolsSection(hasFullAdminAccess, hasOverrideEscapeHatch));
    }

    // Add Developer Tools section if user is a developer
    const devSections = getFilteredAdminNavSections(isVerityAdmin, isTechAdmin, isInitiallyDeveloper);
    if (devSections.length > 0) {
      const devItems: SideNavigationProps.Link[] = devSections.flatMap((section) =>
        section.items.map((item) => ({
          type: 'link' as const,
          text: item.text,
          href: item.href,
        }))
      );
      if (devItems.length > 0) {
        items.push({
          type: 'section',
          text: 'Developer Tools',
          items: devItems,
        });
      }
    }

    // Add Support section
    items.push(buildSupportSection());

    return items;
  }, [
    activeReports,
    userPermission,
    showAdminSection,
    hasFullAdminAccess,
    hasOverrideEscapeHatch,
    isVerityAdmin,
    isTechAdmin,
    isInitiallyDeveloper,
  ]);

  // Resolve activeHref: find the best matching nav link for the current pathname
  // This ensures child routes (e.g. /reports-configuration/edit/X) highlight the parent nav item
  const resolvedActiveHref = useMemo(() => {
    const collectHrefs = (items: SideNavigationProps.Item[]): string[] => {
      const hrefs: string[] = [];
      for (const item of items) {
        if ('href' in item && item.href) hrefs.push(item.href);
        if ('items' in item && item.items) hrefs.push(...collectHrefs(item.items as SideNavigationProps.Item[]));
      }
      return hrefs;
    };
    const allHrefs = collectHrefs(navigationItems);
    if (allHrefs.includes(pathname)) return pathname;
    return allHrefs.filter((href) => pathname.startsWith(href)).sort((a, b) => b.length - a.length)[0] || pathname;
  }, [pathname, navigationItems]);

  const handleFollow = useCallback(
    (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
      // Skip navigation for non-navigable items (empty href)
      if (!event.detail.href) {
        event.preventDefault();
        return;
      }

      // Allow external links to open in new tab with default browser behavior
      if (event.detail.external) {
        return;
      }

      // For internal links, prevent default and use React Router navigation
      event.preventDefault();
      navigatePreservingParams(navigate, event.detail.href);
    },
    [navigate]
  );

  return (
    <SideNavigation
      data-testid="side-navigation"
      activeHref={resolvedActiveHref}
      onFollow={handleFollow}
      items={navigationItems}
    />
  );
};

export default HomePageSideNavigation;
